import { motion } from "framer-motion";
import { Car, Truck, Users, Heart } from "lucide-react";

interface CityUseCasesProps {
  cityName: string;
  driveTime: string;
}

const getUseCases = (cityName: string) => [
  {
    icon: Car,
    title: `Vous êtes VTC à ${cityName} ?`,
    description: `Votre véhicule roule beaucoup (Uber, Bolt, Chauffeur privé). L'usure est accélérée. Nos créneaux matinaux dès 8h30 et nos rendez-vous rapides sont parfaits pour minimiser votre immobilisation. Vous êtes libre en 45 min max.`,
  },
  {
    icon: Truck,
    title: `Vous êtes artisan/livreur à ${cityName} ?`,
    description: `Votre fourgon ou utilitaire est votre outil de travail. Nous comprenons l'urgence. Créneaux prioritaires pour les professionnels. Parking adapté aux utilitaires. Résultat immédiat pour reprendre la route au plus vite.`,
  },
  {
    icon: Users,
    title: `Vous êtes famille à ${cityName} ?`,
    description: `Votre voiture fait les trajets école-travail-courses au quotidien. Usure modérée mais régulière. Nos conseils de préparation vous évitent 60% des contre-visites. Ouvert le samedi matin pour s'adapter à votre emploi du temps.`,
  },
  {
    icon: Heart,
    title: `Vous êtes retraité à ${cityName} ?`,
    description: `Votre voiture roule peu mais vieillit. Attention à la corrosion et aux pneus qui craquelent avec l'âge. Nos contrôleurs sont patients et expliquent tout clairement. Résultat immédiat, en toute transparence.`,
  },
];

export const CityUseCases = ({ cityName, driveTime }: CityUseCasesProps) => {
  const useCases = getUseCases(cityName);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
            Cas d'usage spécifiques à {cityName}
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            Quel que soit votre profil, nous avons la solution adaptée pour votre contrôle technique à {driveTime} de {cityName}.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {useCases.map((useCase, index) => (
            <motion.div
              key={useCase.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <useCase.icon className="w-6 h-6 text-autosur-blue" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-autosur-navy mb-2 text-sm">
                    {useCase.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-sm leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
